import { useEffect, useRef } from 'react'

/**
 * Custom hook for saving and restoring scroll position in table containers
 * @param {Object} store - The store instance with scrollTop property
 * @param {Array} tableData - Array of table data to watch for changes
 * @param {boolean} isLoading - Loading state
 * @returns {React.RefObject} - Ref to attach to table container
 */
export function useScrollRestoration(store, tableData, isLoading) {
  const tableContainerRef = useRef(null)

  // Restore scroll position after table is rendered and data is loaded
  useEffect(() => {
    if (!isLoading && tableData.length > 0 && store.scrollTop > 0) {
      const restoreScroll = () => {
        const container = tableContainerRef.current
        if (container) {
          container.scrollTop = store.scrollTop
        } else {
          // Fallback: try to find the scrollable container by class or data attribute
          const scrollableContainer = document.querySelector('[data-mrt-table-container]') || 
                                     document.querySelector('.MuiTableContainer-root')
          if (scrollableContainer) {
            scrollableContainer.scrollTop = store.scrollTop
          }
        }
      }
      
      // Try multiple times to ensure DOM is ready
      requestAnimationFrame(() => {
        restoreScroll()
        setTimeout(restoreScroll, 100)
        setTimeout(restoreScroll, 300)
      })
    }
  }, [tableData, isLoading, store])

  return tableContainerRef
}

/**
 * Creates scroll event handler and ref props for MaterialReactTable container
 * @param {Object} store - The store instance with scrollTop property
 * @param {React.RefObject} tableContainerRef - Ref to the table container
 * @returns {Object} - Props object for muiTableContainerProps
 */
export function createScrollContainerProps(store, tableContainerRef) {
  return {
    ref: tableContainerRef,
    onScroll: (e) => {
      // Save scroll position while scrolling
      if (e.target instanceof HTMLElement) {
        store.scrollTop = e.target.scrollTop
      }
    }
  }
}

/**
 * Hook for restoring scroll position when ref is already created in component
 * @param {React.RefObject} tableContainerRef - Ref to the table container (created in component)
 * @param {Object} store - The store instance with scrollTop property
 * @param {Array} tableData - Array of table data to watch for changes
 * @param {boolean} isLoading - Loading state
 */
export function useRestoreScrollPosition(tableContainerRef, store, tableData, isLoading) {
  useEffect(() => {
    if (!isLoading && tableData.length > 0 && store.scrollTop > 0) {
      const restoreScroll = () => {
        const container = tableContainerRef.current
        if (container) {
          container.scrollTop = store.scrollTop
        } else {
          const scrollableContainer = document.querySelector('[data-mrt-table-container]') || 
                                     document.querySelector('.MuiTableContainer-root')
          if (scrollableContainer) {
            scrollableContainer.scrollTop = store.scrollTop
          }
        }
      }
      
      requestAnimationFrame(() => {
        restoreScroll()
        setTimeout(restoreScroll, 100)
        setTimeout(restoreScroll, 300)
      })
    }
  }, [tableData, isLoading, store, tableContainerRef])
}

/**
 * Creates row click handler that saves scroll position before navigation
 * @param {Object} store - The store instance with scrollTop property
 * @param {React.RefObject} tableContainerRef - Ref to the table container
 * @param {Function} navigateCallback - Function to call for navigation (e.g., () => navigate('/path'))
 * @returns {Function} - Row click handler
 */
export function createRowClickHandler(store, tableContainerRef, navigateCallback) {
  return () => {
    // Save scroll position before navigation
    if (tableContainerRef.current) {
      store.scrollTop = tableContainerRef.current.scrollTop
    }
    navigateCallback()
  }
}

